import { Observable } from 'rxjs';

/**
 * Proveedor que atiende la firma de PDF.
 * - platform: API nueva (tfnbx-platform /documents/sign/...).
 * - legacy: API vieja (TFMDocumentos/FrmPdfSign/...), usada por formularios
 *   que luego se integran con el sistema legacy.
 */
export type PdfSignProvider = 'platform' | 'legacy';

export const DEFAULT_PDF_SIGN_PROVIDER: PdfSignProvider = 'platform';

/** Clave que puede declarar el objeto `pdf` del esquema para elegir proveedor. */
export const PDF_SIGN_PROVIDER_SCHEMA_KEY = 'signProvider';

export interface PdfSignParams {
  typeDocument: string;
  idDocument: string;
  tdoc: string;
  ndoc: string;
}

export interface PdfSignOtpParams extends PdfSignParams {
  data: any;
}

export interface PdfSignUploadParams extends PdfSignParams {
  file: File;
}

/**
 * Respuesta que espera el componente `sf-sign` de sf-pdfsign para
 * verifyCodeOTP / uploadSignedDocument.
 */
export interface PdfSignResult {
  Codigo: string;
  Descripcion?: string;
  Token?: string;
  Validado?: boolean;
  FirmasPendientes?: any;
  [key: string]: any;
}

/**
 * Contrato de cada proveedor. Solo arma URLs, hace la llamada HTTP y
 * normaliza la respuesta. Lo transversal (reintentos, estado de carga,
 * espera de submission) vive en DocumentService.
 */
export interface PdfSignStrategy {
  readonly provider: PdfSignProvider;

  setEnviroments(enviroment: any): void;

  /**
   * Descarga el PDF como Blob. `path` es lo que sf-pdfsign envia
   * (configDocument.Ruta); `signedDocument` es la ultima respuesta de
   * getDococumentSign, por si el proveedor prefiere otra URL.
   */
  getBlobDocument(path: string, signedDocument?: any): Observable<Blob>;

  /** Devuelve el objeto documento (Ruta, Estado, jCondiciones, celular, eMail...). */
  getDococumentSign(idkatios: string, params: PdfSignParams): Observable<any>;

  sendCodeOTP(idkatios: string, params: PdfSignOtpParams): Observable<any>;

  verifyCodeOTP(
    idkatios: string,
    params: PdfSignOtpParams,
  ): Observable<PdfSignResult>;

  uploadSignedDocument(
    idkatios: string,
    params: PdfSignUploadParams,
  ): Observable<PdfSignResult>;
}

export function normalizePdfSignProvider(value: unknown): PdfSignProvider {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return normalized === 'legacy' ? 'legacy' : DEFAULT_PDF_SIGN_PROVIDER;
}

/**
 * Busca `signProvider` dentro de los esquemas del CRUD. Recorre las
 * propiedades de forma recursiva y devuelve el primer valor encontrado.
 * Si ningun esquema lo declara devuelve el proveedor por defecto.
 */
export function resolvePdfSignProviderFromSchemas(
  schemas: any[] | null | undefined,
): PdfSignProvider {
  const found = findSchemaKey(schemas, PDF_SIGN_PROVIDER_SCHEMA_KEY);
  return normalizePdfSignProvider(found);
}

function findSchemaKey(node: any, key: string, depth = 0): unknown {
  if (!node || depth > 12) return undefined;

  if (Array.isArray(node)) {
    for (const item of node) {
      const value = findSchemaKey(item, key, depth + 1);
      if (value !== undefined) return value;
    }
    return undefined;
  }

  if (typeof node !== 'object') return undefined;

  if (node[key] !== undefined && node[key] !== null && node[key] !== '') {
    return node[key];
  }

  const children = [node.properties, node.items];
  for (const child of children) {
    if (!child || typeof child !== 'object') continue;
    for (const def of Object.values<any>(child)) {
      const value = findSchemaKey(def, key, depth + 1);
      if (value !== undefined) return value;
    }
  }

  return undefined;
}
